import { IDEModeProvider } from '@common/ide/IdeMode';
import { UnloadHandler } from '@common/ide/UnloadHandler';
import { IDELoader } from '@common/IDELoader';
import { ProjectDetail } from '@common/queries/project';
import { EditMode } from '@common/types';
import { BaseLSPClient } from '@LSP/base/BaseLSPClient';
import { BaseLSPProvider } from '@LSP/base/BaseLSPContext';
import { DiagnosticProvider } from '@LSP/DiagnosticProvider';
import { Stack, StackItem } from '@prophecy/ui-v3';
import { GraphProvider } from '@prophecy/ui-v3/Graph/GraphContext';
import { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import styled from 'styled-components';

import { LogProvider, useLoggerState } from '../../generic-components/Logger/RuntimeLogger';
import { CanvasRootRootCls } from '../../layout/config';
import { getLineageEditorWSEndPoint } from '../../utils/getServerUrl';
import { isWebsocketCompressionEnabled } from '../../utils/localstorage-keys';
import { Canvas as LinageGraph } from '../Lineage/Dataset/SummaryView';
import { useLineageEditor } from '../Lineage/hook';
import { ProjectLineageFooter } from './Footer';
import { Header } from './Header';
const CanvasContainer = styled(Stack)`
  position: relative;
`;

function Canvas({ project }: { project: ProjectDetail }) {
  return (
    <CanvasContainer className={CanvasRootRootCls}>
      <Header project={project} />
      <StackItem grow='1'>
        <GraphProvider>
          <LinageGraph />
        </GraphProvider>
      </StackItem>
      <IDELoader />
    </CanvasContainer>
  );
}

export function Lineage({ project }: { project: ProjectDetail }) {
  const { _id: projectId } = project;
  const lspClient = useMemo(
    () =>
      new BaseLSPClient(getLineageEditorWSEndPoint(), {
        compressionEnabled: isWebsocketCompressionEnabled
      }),
    []
  );
  const { lineageStore } = useLineageEditor(lspClient, projectId, true);
  const loggerState = useLoggerState();

  useEffect(() => {
    return () => {
      lspClient.detach();
    };
  }, [lspClient]);

  return (
    <BaseLSPProvider client={lspClient}>
      <DiagnosticProvider>
        <Provider store={lineageStore}>
          <UnloadHandler />
          <LogProvider value={loggerState}>
            <IDEModeProvider value={EditMode.enabled}>
              <Canvas project={project} />
              <ProjectLineageFooter projectId={projectId} />
            </IDEModeProvider>
          </LogProvider>
        </Provider>
      </DiagnosticProvider>
    </BaseLSPProvider>
  );
}
